/**
 * Build a shopping list.
 *
 * Only the serving counts a recipe actually publishes are offered — quantities are read from the
 * stored yields table, never scaled in the browser (PLAN.md §2.4).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { PageShell } from '../components/PageShell';
import { createList, fetchCollection, type SessionUser } from '../lib/api';
import { useAsync } from '../lib/useAsync';

export function NewListPage({ user, onSignOut }: { user: SessionUser; onSignOut: () => void }) {
  const navigate = useNavigate();
  const collection = useAsync(() => fetchCollection(), []);

  const [name, setName] = useState('Shopping list');
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = useMemo(() => Object.entries(selected), [selected]);

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      const created = await createList(
        name.trim() === '' ? 'Shopping list' : name.trim(),
        chosen.map(([recipeId, servings]) => ({ recipeId, servings })),
      );
      navigate(`/lists/${created.id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not build the list');
      setSaving(false);
    }
  }

  return (
    <PageShell
      user={user}
      onSignOut={onSignOut}
      title="Build a shopping list"
      actions={
        <Button
          radius="xl"
          loading={saving}
          disabled={chosen.length === 0}
          onClick={() => void handleCreate()}
        >
          Create list ({chosen.length})
        </Button>
      }
    >
      <TextInput
        label="List name"
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
        maw={420}
      />

      {error ? (
        <Alert color="red" variant="light" role="alert">
          {error}
        </Alert>
      ) : null}

      {collection.loading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : null}

      {collection.data && collection.data.length === 0 ? (
        <Alert color="blue" variant="light" title="No recipes yet">
          Import a recipe first, then you can build a shopping list from it.
        </Alert>
      ) : null}

      <Stack gap="sm">
        {(collection.data ?? []).map((item) => {
          const isSelected = selected[item.id] !== undefined;
          return (
            <Card key={item.id} withBorder radius="lg" padding="md">
              <Group justify="space-between" wrap="nowrap" gap="sm" align="center">
                <Checkbox
                  checked={isSelected}
                  onChange={(event) => {
                    const next = { ...selected };
                    if (event.currentTarget.checked) next[item.id] = item.defaultServings;
                    else delete next[item.id];
                    setSelected(next);
                  }}
                  label={
                    <Stack gap={0}>
                      <Text fw={600} size="sm">
                        {item.name}
                      </Text>
                      {item.isPartial ? (
                        <Text size="xs" c="var(--ff-amber-600)">
                          Reduced detail — serving sizes unavailable
                        </Text>
                      ) : null}
                    </Stack>
                  }
                  styles={{ body: { alignItems: 'center' } }}
                />
                {isSelected ? (
                  <NumberInput
                    aria-label={`Servings for ${item.name}`}
                    value={selected[item.id]}
                    onChange={(value) =>
                      setSelected((current) => ({
                        ...current,
                        [item.id]: typeof value === 'number' ? value : Number(value) || 1,
                      }))
                    }
                    min={1}
                    max={12}
                    w={110}
                    suffix=" serv."
                  />
                ) : null}
              </Group>
            </Card>
          );
        })}
      </Stack>

      {chosen.length > 0 ? (
        <Text size="xs" c="var(--ff-muted)">
          Serving counts outside what a recipe publishes are adjusted to the nearest published size.
        </Text>
      ) : null}
    </PageShell>
  );
}
